export const HistogramPlotControl = (selectedData) => {
  // Helper: Limit a number to 2 decimal places.
  const limitDecimal = (value) => parseFloat(value.toFixed(2));

  // Helper functions for rounding based on step size, then limit decimals.
  const roundDownToStep = (value, step) =>
    limitDecimal(Math.floor(value / step) * step);
  const roundUpToStep = (value, step) =>
    limitDecimal(Math.ceil(value / step) * step);

  // === Data extraction ===
  const values = [];
  let title;
  selectedData.forEach((row, rowIndex) => {
    if (row[0] && !isNaN(parseFloat(row[0].m))) {
      values.push({ binned_Frequency: parseFloat(row[0].m) });
    } else if (rowIndex === 0 && row[0]) {
      title = row[0].m;
    }
  });
  if (values.length === 0) return null;

  // === Compute basic stats ===
  const dataValues = values.map((d) => d.binned_Frequency);
  const minValue = Math.min(...dataValues);
  const maxValue = Math.max(...dataValues);
  const dataRange = maxValue - minValue;

  // smallest positive difference
  const sorted = dataValues.slice().sort((a, b) => a - b);
  let minDiff = Infinity;
  for (let i = 1; i < sorted.length; i++) {
    const diff = sorted[i] - sorted[i - 1];
    if (diff > 0 && diff < minDiff) minDiff = diff;
  }
  if (minDiff === Infinity) minDiff = 1;
  minDiff = limitDecimal(minDiff);

  // default bin size ~1/4 range, rounded down
  const defaultBinSize = roundDownToStep(dataRange / 4, minDiff);
  const binSize = Math.max(minDiff, Math.min(defaultBinSize, dataRange));

  // slider bounds for ranges
  const rangeMinLowerBound = minValue - dataRange * 4;
  const rangeMinUpperBound = maxValue - minDiff;
  const rangeMaxLowerBound = minValue + minDiff;
  const rangeMaxUpperBound = maxValue + dataRange * 4;

  // default range bounds
  const defaultBinMin = roundDownToStep(minValue, minDiff);
  const defaultBinMax = roundUpToStep(maxValue, minDiff);
  const binRangeMin = Math.max(
    rangeMinLowerBound,
    Math.min(defaultBinMin, rangeMinUpperBound)
  );
  const binRangeMax = Math.max(
    rangeMaxLowerBound,
    Math.min(defaultBinMax, rangeMaxUpperBound)
  );

  // === Step‑clamping with MAX_STEPS ===
  const MAX_STEPS = 300;
  const computeClampedStep = (sliderMin, sliderMax) => {
    const range = sliderMax - sliderMin;
    const ideal = range / MAX_STEPS;
    const raw = Math.max(minDiff, ideal);
    return limitDecimal(Math.ceil(raw / minDiff) * minDiff);
  };

  const stepForBinSize = computeClampedStep(minDiff, dataRange);
  const stepForBinRangeMin = computeClampedStep(
    rangeMinLowerBound,
    rangeMinUpperBound
  );
  const stepForBinRangeMax = computeClampedStep(
    rangeMaxLowerBound,
    rangeMaxUpperBound
  );

  // The tooltip label still shows the true bin range.
  return {
    spec: {
      $schema: "https://vega.github.io/schema/vega-lite/v5.json",
      padding: 10,
      autosize: {
        type: "fit",
        resize: true,
        contains: "padding",
      },
      config: {
        bar: {
          binSpacing: 5,
        },
      },
      params: [
        {
          name: "histogramBinWidth",
          value: binSize,
          bind: {
            input: "range",
            min: minDiff,
            max: dataRange,
            step: stepForBinSize,
            name: "Bin Width:", // can be translated with intl.formatMessage
          },
        },
        {
          name: "histogramRangeMin",
          value: binRangeMin,
          bind: {
            input: "range",
            min: rangeMinLowerBound,
            max: rangeMinUpperBound,
            step: stepForBinRangeMin,
            name: "Min Range:", // can be translated with intl.formatMessage
          },
        },
        {
          name: "histogramRangeMax",
          value: binRangeMax,
          bind: {
            input: "range",
            min: rangeMaxLowerBound,
            max: rangeMaxUpperBound,
            step: stepForBinRangeMax,
            name: "Max Range:", // can be translated with intl.formatMessage
          },
        },
      ],
      data: {
        values: values,
      },
      transform: [
        {
          calculate:
            "histogramRangeMin + floor((datum.binned_Frequency - histogramRangeMin) / histogramBinWidth) * histogramBinWidth",
          as: "binStart",
        },
        {
          calculate:
            "min(histogramRangeMax, datum.binStart + histogramBinWidth)",
          as: "binEnd",
        },
        {
          calculate: "datum.binStart + (datum.binEnd - datum.binStart) * 0.99",
          as: "barEnd",
        },
        {
          filter:
            "datum.binned_Frequency >= histogramRangeMin && datum.binned_Frequency < histogramRangeMax",
        },
      ],
      layer: [
        {
          params: [
            {
              name: "hover",
              select: {
                type: "point",
                on: "pointerover",
                clear: "pointerout",
              },
            },
          ],
          mark: {
            type: "bar",
            color: "#eee",
            tooltip: true,
            clip: true,
          },
          encoding: {
            x: {
              field: "binStart",
              type: "quantitative",
              scale: {
                domain: [
                  { signal: "histogramRangeMin" },
                  { signal: "histogramRangeMax" },
                ],
                nice: false,
              },
              axis: {
                title: null,
                labelAngle: -45,
              },
            },
            x2: {
              field: "binEnd",
            },
            y: {
              aggregate: "count",
              axis: {
                title: null,
              },
            },
            tooltip: [
              {
                field: "binStart",
                type: "quantitative",
                title: "Bin Start", // this string can be translated with intl.formatMessage
                format: ".2f",
              },
              {
                field: "binEnd",
                type: "quantitative",
                title: "Bin End", // this string can be translated with intl.formatMessage
                format: ".2f",
              },
              {
                aggregate: "count",
                type: "quantitative",
                title: "Count", // this string can be translated with intl.formatMessage
              },
            ],
            opacity: {
              condition: {
                test: { param: "hover", empty: false },
                value: 0.5,
              },
              value: 0,
            },
            detail: [{ aggregate: "count" }],
          },
        },
        {
          mark: {
            type: "bar",
            color: "#003e6b",
            clip: true,
          },
          encoding: {
            x: {
              field: "binStart",
              type: "quantitative",
              scale: {
                domain: [
                  { signal: "histogramRangeMin" },
                  { signal: "histogramRangeMax" },
                ],
                nice: false,
              },
              axis: {
                title: null,
              },
            },
            x2: {
              field: "barEnd",
            },
            y: {
              aggregate: "count",
              axis: {
                title: null,
              },
            },
          },
        },
      ],
    },
    title,
    histogramBinWidth: binSize,
    histogramRangeMin: binRangeMin,
    histogramRangeMax: binRangeMax,
  };
};

// Helper function: Attach event handlers to the range sliders
// using pointer events in the capture phase to stop propagation.
export const attachRangeSliderEventHandlers = (vegaView) => {
  // Ensure the view's DOM elements are rendered.
  vegaView.runAsync().then(() => {
    // Find slider elements within the Vega container.
    const sliders = document.querySelectorAll('input[type="range"]');
    sliders.forEach((slider) => {
      // Disable native dragging.
      slider.setAttribute("draggable", "false");

      // Stop pointer events in the capture phase.
      slider.addEventListener(
        "pointerdown",
        (e) => {
          e.stopPropagation();
        },
        true
      );
      slider.addEventListener(
        "pointermove",
        (e) => {
          e.stopPropagation();
        },
        true
      );
      slider.addEventListener(
        "pointerup",
        (e) => {
          e.stopPropagation();
        },
        true
      );
    });
  });
};
